import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, getComicsByCategory } from '@/mocks/comics';

export default function Category() {
  const { t } = useTranslation();
  const { slug = 'all' } = useParams<{ slug: string }>();

  const filteredComics = useMemo(() => getComicsByCategory(slug), [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug],
  );

  const categoryName = currentCategory?.name || t('category.all');
  const pageTitle = slug === 'all'
    ? '韩漫漫画大全 - 全部韩国漫画分类浏览'
    : `${categoryName}韩漫 - 好看的${categoryName}韩国漫画在线`;

  const breadcrumbItems = [
    { label: t('breadcrumb.category'), path: '/category/all' },
    { label: categoryName },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: pageTitle,
          description: `浏览${categoryName}分类的韩国漫画，韩漫漫画网站为你提供海量${categoryName}在线漫画资源。`,
          url: window.location.origin + `/category/${slug}`,
          isPartOf: { '@type': 'WebSite', name: '韩漫漫画网站', url: window.location.origin },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                {slug === 'all' ? '漫画大全' : `${categoryName}韩漫`}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                {slug === 'all'
                  ? '探索全部韩国漫画分类，发现属于你的精彩作品。韩漫漫画网站持续更新，让你畅享免费在线漫画阅读体验。'
                  : `浏览最热门的${categoryName}韩国漫画，发现好看的${categoryName}在线漫画。韩漫漫画网站为你精选优质的${categoryName}作品。`}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-nowrap">
              <div className="flex items-center bg-background-100 rounded-full px-1 py-1 gap-1 flex-nowrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Comic Grid */}
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium whitespace-nowrap"
                >
                  查看全部漫画
                </Link>
              </div>
            )}

            {/* Pagination Placeholder */}
            {filteredComics.length > 0 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button className="w-9 h-9 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center text-sm hover:bg-primary-50 hover:text-primary-500 transition-colors">
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <button className="w-9 h-9 rounded-full bg-primary-500 text-white flex items-center justify-center text-sm font-medium">
                  1
                </button>
                <button className="w-9 h-9 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center text-sm hover:bg-primary-50 hover:text-primary-500 transition-colors">
                  2
                </button>
                <button className="w-9 h-9 rounded-full bg-background-100 text-foreground-600 flex items-center justify-center text-sm hover:bg-primary-50 hover:text-primary-500 transition-colors">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}